// WikiAsk.jsx — cross-department "Ask the Wiki" with a department scope selector.
import React, { useRef, useEffect, useState } from 'react';
import { ArrowLeft, Filter, Check, Sparkles } from 'lucide-react';
import { WIKI_DEPTS, deptColors } from '../../lib/wikiConstants';
import { useWikiApi } from '../../hooks/useWiki';
import { WikiAiAvatar } from './WikiCiteChip';
import { Bubble, Composer, useWikiChat } from './WikiChat';

export default function WikiAsk({ accessMap, onOpenDoc, onBack, userName }) {
    const api = useWikiApi();
    const accessible = WIKI_DEPTS.filter(d => accessMap?.[d.id]);
    const [scope, setScope] = useState(() => accessible.slice(0, 3).map(d => d.id));
    const { msgs, send } = useWikiChat((text) => api.ask({ depts: scope, question: text }), 'selected departments');
    const bodyRef = useRef(null);
    useEffect(() => { if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight; }, [msgs]);
    const toggle = (id) => setScope(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

    const SUGGESTIONS = [
        { q: 'A customer is unhappy about a price increase and wants a refund — what do I do?', depts: ['service', 'sales', 'marketing'] },
        { q: 'What’s our policy on time off and how do I request it?', depts: ['company'] },
    ];

    return (
        <div className="h-full flex flex-col bg-stone-50">
            <div className="h-14 px-5 flex items-center gap-3 border-b border-stone-200 bg-white">
                <button onClick={onBack} className="w-8 h-8 rounded-lg hover:bg-stone-100 text-stone-600 flex items-center justify-center"><ArrowLeft className="w-4 h-4" /></button>
                <WikiAiAvatar size={32} />
                <div className="flex-1 min-w-0">
                    <div className="text-[14px] wiki-display font-bold text-stone-900">Ask the Wiki</div>
                    <div className="text-[11px] text-stone-500">Search across every department you can access — cited, with links.</div>
                </div>
            </div>

            <div className="px-5 py-2.5 border-b border-stone-200 bg-white flex items-center gap-2 flex-wrap">
                <span className="text-[11px] font-semibold text-stone-500 inline-flex items-center gap-1"><Filter className="w-3.5 h-3.5" /> Scope:</span>
                {accessible.map(d => {
                    const on = scope.includes(d.id);
                    const c = deptColors(d.id);
                    return (
                        <button key={d.id} onClick={() => toggle(d.id)}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${on ? c.chipOn : 'bg-white border-stone-200 text-stone-400'}`}>
                            {on ? <Check className="w-3 h-3" /> : <d.icon className="w-3 h-3" />}{d.name}
                        </button>
                    );
                })}
                <span className="text-[11px] text-stone-400 ml-auto">{scope.length} of {accessible.length} departments</span>
            </div>

            <div ref={bodyRef} className="flex-1 overflow-auto p-5 space-y-4">
                {msgs.length === 0 ? (
                    <div className="max-w-[600px] mx-auto pt-6">
                        <div className="text-center">
                            <div className="flex justify-center"><WikiAiAvatar size={44} /></div>
                            <div className="wiki-display font-bold text-stone-900 text-[18px] mt-3">One question, every department</div>
                            <div className="text-[13px] text-stone-500 mt-1">Pick the spaces to search above, then ask. The agent only ever reads pages your role can open.</div>
                        </div>
                        <div className="mt-5 space-y-2">
                            {SUGGESTIONS.map((s, i) => (
                                <button key={i} onClick={() => send(s.q)} className="w-full text-left px-3.5 py-2.5 rounded-xl border border-stone-200 bg-white hover:border-stone-400 hover:bg-stone-100/30 text-[13px] text-stone-600 flex items-start gap-2">
                                    <Sparkles className="w-4 h-4 text-stone-600 shrink-0 mt-0.5" />
                                    <span className="flex-1">{s.q}<span className="block text-[10px] text-stone-400 mt-1">spans {s.depts.map(x => WIKI_DEPTS.find(d => d.id === x)?.name).join(' · ')}</span></span>
                                </button>
                            ))}
                        </div>
                    </div>
                ) : msgs.map((m, i) => <Bubble key={i} m={m} userName={userName} onOpenDoc={onOpenDoc} />)}
            </div>
            <Composer onSend={send} placeholder="Ask across departments…" disabled={scope.length === 0} />
        </div>
    );
}
